import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Phone, Loader2, UserCheck } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { getFirstTouchAcquisition, trackPersistedLead } from "@/lib/acquisitionTracking";
import { doctorNameFreeText, gentleFollowUp } from "@shared/chatConversationStyle";

const BRAND_GOLD = "#D4A843";
const CLINIC_PHONE = "+971 4 296 4442";
const CHAT_SESSION_KEY = "bella-rose-chat-session-v2";

interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: number;
  leadCollected?: boolean;
}

export function ChatBot() {
  const { lang, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [visitorToken, setVisitorToken] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [leadSaved, setLeadSaved] = useState(false);
  const [leadScore, setLeadScore] = useState<"hot" | "warm" | "cold">("cold");
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const acquisitionRef = useRef(getFirstTouchAcquisition());
  const startInFlightRef = useRef(false);
  const sendInFlightRef = useRef(false);
  const pendingTurnRef = useRef<{ id: string; content: string; history: { role: "user" | "assistant"; content: string }[] } | null>(null);

  const startSessionMutation = trpc.chat.startSession.useMutation();
  const sendMessageMutation = trpc.chat.sendMessage.useMutation();
  const notifyChatMutation = trpc.chat.notifyChatStart.useMutation();
  const saveLeadMutation = trpc.chat.saveLead.useMutation();
  const saveFollowUpMutation = trpc.chat.saveFollowUp.useMutation();
  const utils = trpc.useUtils();

  useEffect(() => {
    const saved = window.sessionStorage.getItem(CHAT_SESSION_KEY);
    if (!saved) return;
    let session: { sessionId: number; visitorToken: string };
    try { session = JSON.parse(saved); } catch { window.sessionStorage.removeItem(CHAT_SESSION_KEY); return; }
    if (!Number.isSafeInteger(session.sessionId) || !session.visitorToken) {
      window.sessionStorage.removeItem(CHAT_SESSION_KEY);
      return;
    }
    startInFlightRef.current = true;
    setIsConnecting(true);
    utils.chat.resumeSession.fetch(session).then(result => {
      setSessionId(result.sessionId);
      setVisitorToken(session.visitorToken);
      setAgentName(result.agentName);
      setMessages(result.messages.map(message => ({ ...message })));
      setLeadSaved(result.leadCollected);
      setFirstMessageSent(result.messages.some(message => message.role === "user"));
      setSessionStarted(true);
      lastUserMsgCount.current = result.messages.filter(message => message.role === "user").length;
    }).catch(() => {
      window.sessionStorage.removeItem(CHAT_SESSION_KEY);
      setConnectionError(true);
    }).finally(() => {
      startInFlightRef.current = false;
      setIsConnecting(false);
    });
  }, [utils]);

  // Get current page path
  const getSourcePage = () => {
    try {
      return window.location.pathname;
    } catch {
      return "/";
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping, isConnecting]);

  // Start session via API — gets contextual greeting based on page and language
  const startSession = useCallback(async () => {
    if (sessionStarted || startInFlightRef.current) return;
    startInFlightRef.current = true;
    setIsConnecting(true);
    setConnectionError(false);

    try {
      const result = await startSessionMutation.mutateAsync({
        sourcePage: getSourcePage(),
        language: lang as "ar" | "en",
        ...acquisitionRef.current,
      });

      setAgentName(result.agentName);
      setSessionId(result.sessionId);
      setVisitorToken(result.visitorToken);
      window.sessionStorage.setItem(CHAT_SESSION_KEY, JSON.stringify({ sessionId: result.sessionId, visitorToken: result.visitorToken }));
      setSessionStarted(true);
      setIsConnecting(false);

      // Show contextual greeting from backend
      setMessages([
        {
          role: "assistant",
          content: result.greeting,
          timestamp: Date.now(),
        },
      ]);
    } catch (error) {
      console.error("Error starting session:", error);
      setIsConnecting(false);
      setConnectionError(true);
    } finally {
      startInFlightRef.current = false;
    }
  }, [sessionStarted, lang]);

  // Open chat and start session
  const handleOpen = () => {
    setIsOpen(true);
    setHasUnread(false);
    if (!sessionStarted) {
      startSession();
    }
    setTimeout(() => inputRef.current?.focus(), 300);
  };

  // Send message
  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isTyping || isConnecting || sendInFlightRef.current || !sessionId || !visitorToken) return;
    sendInFlightRef.current = true;
    setSendError(false);

    const retry = pendingTurnRef.current?.content === trimmed ? pendingTurnRef.current : null;
    const turn = retry ?? {
      id: crypto.randomUUID(),
      content: trimmed,
      history: [...messages, { role: "user" as const, content: trimmed, timestamp: Date.now() }]
        .filter(m => m.role !== "system")
        .slice(-60)
        .map(m => ({ role: m.role as "user" | "assistant", content: m.content })),
    };
    pendingTurnRef.current = turn;

    if (!retry) setMessages((prev) => [...prev, { role: "user", content: trimmed, timestamp: Date.now() }]);
    setInput("");
    setIsTyping(true);

    // Build conversation history for API
    try {
      const result = await sendMessageMutation.mutateAsync({
        messages: turn.history,
        sessionId,
        visitorToken,
        clientMessageId: turn.id,
        sourcePage: getSourcePage(),
        ...acquisitionRef.current,
      });
      if (!firstMessageSent) {
        setFirstMessageSent(true);
        notifyChatMutation.mutate({ sessionId, visitorToken });
      }

      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: result.message,
        timestamp: Date.now(),
        leadCollected: result.leadCollected,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      pendingTurnRef.current = null;

      // Update lead score
      if (result.leadScore) {
        setLeadScore(result.leadScore as "hot" | "warm" | "cold");
      }

      // Track if a lead was collected
      if (result.leadCollected) {
        setLeadSaved(true);
        trackPersistedLead({
          token: result.leadId ? `chat-lead-${result.leadId}` : `chat-session-${sessionId || "unknown"}`,
          service: "Chat enquiry",
          location: getSourcePage(),
          method: "chat",
        });
      }

      // Show unread badge if chat is closed
      if (!isOpen) {
        setHasUnread(true);
      }
    } catch (error) {
      setSendError(true);
      setInput(trimmed);
    } finally {
      setIsTyping(false);
      sendInFlightRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick contact form submission
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const handleContactSubmit = async () => {
    if (!contactPhone.trim() || !sessionId || !visitorToken || saveLeadMutation.isPending) return;

    const result = await saveLeadMutation.mutateAsync({
      name: contactName || undefined,
      phone: contactPhone,
      serviceInterest: messages
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join(" ")
        .slice(0, 200),
      conversationSummary: messages
        .slice(-6)
        .map((m) => `${m.role === "user" ? "👤" : "🏥"}: ${m.content}`)
        .join("\n"),
      sessionId,
      visitorToken,
      sourcePage: getSourcePage(),
      ...acquisitionRef.current,
    });

    if (result.success) {
      setLeadSaved(true);
      const fallbackToken = typeof globalThis.crypto?.randomUUID === "function"
        ? globalThis.crypto.randomUUID()
        : `chat-form-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      trackPersistedLead({
        token: result.leadId ? `chat-lead-${result.leadId}` : fallbackToken,
        service: "Chat contact request",
        location: getSourcePage(),
        method: "chat",
      });
      setShowContactForm(false);
      if (result.confirmation) setMessages((prev) => [...prev, { role: "assistant", content: result.confirmation, timestamp: Date.now() }]);
    }
  };

  // Lead score badge
  const getLeadScoreBadge = () => {
    if (leadScore === "hot") return { emoji: "🔥", color: "text-red-500" };
    if (leadScore === "warm") return { emoji: "🟡", color: "text-yellow-500" };
    return null;
  };

  const scoreBadge = getLeadScoreBadge();
  const isRtl = lang === "ar";

  // ─── 30-second inactivity follow-up (context-aware) ─────────────────
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followUpSentRef = useRef(false);

  // One non-question reminder per session; never repeat a pending question or promote offers.
  const getContextFollowUp = useCallback(() => gentleFollowUp(messages, lang), [messages, lang]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    // Only set timer if session is active, chat is open, not typing, and follow-up not already sent
    if (sessionStarted && isOpen && !isTyping && !isConnecting && messages.length > 1 && !followUpSentRef.current) {
      inactivityTimerRef.current = setTimeout(async () => {
        if (!sessionId || !visitorToken) return;
        const contextMsg = getContextFollowUp();
        if (!contextMsg) return;
        followUpSentRef.current = true;
        try {
          const result = await saveFollowUpMutation.mutateAsync({
            sessionId, visitorToken, clientMessageId: crypto.randomUUID(), content: contextMsg,
          });
          if (result.content) setMessages((prev) => [...prev, { role: "assistant", content: result.content!, timestamp: Date.now() }]);
          followUpSentRef.current = true;
        } catch (error) {
          console.error("Could not save chat follow-up:", error);
        }
      }, 45000);
    }
  }, [sessionStarted, sessionId, visitorToken, isOpen, isTyping, isConnecting, messages.length, lang, getContextFollowUp]);

  // Track visitor turns without resetting the one-per-session reminder.
  const lastUserMsgCount = useRef(0);
  useEffect(() => {
    const currentUserMsgCount = messages.filter(m => m.role === "user").length;
    if (currentUserMsgCount > lastUserMsgCount.current) {
      // User sent a new message — reset follow-up flag
      lastUserMsgCount.current = currentUserMsgCount;
      // Do not re-arm unsolicited reminders after each visitor reply.
    }
    resetInactivityTimer();
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [messages.length, resetInactivityTimer]);

  // Clear timer when chat is closed
  useEffect(() => {
    if (!isOpen && inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  }, [isOpen]);

  return (
    <>
      {/* Floating Chat Now Button — replaces WhatsApp button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ delay: 1.5 }}
            onClick={handleOpen}
            className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[9999] flex items-center gap-2.5 px-5 py-3.5 rounded-full shadow-2xl hover:scale-105 transition-all group"
            style={{ backgroundColor: BRAND_GOLD }}
            aria-label="Chat Now"
          >
            <MessageCircle className="w-5 h-5 text-white" />
            <span className="font-medium text-sm text-white">
              {t("Chat Now", "تحدث معنا")}
            </span>
            {hasUnread && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[9999] w-[360px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100"
            dir={isRtl ? "rtl" : "ltr"}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 text-white"
              style={{ backgroundColor: BRAND_GOLD }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold flex items-center gap-1.5">
                    {t("Bella Rose Center", "مركز بيلا روز")}
                    {scoreBadge && (
                      <span className="text-xs" title={`Lead: ${leadScore}`}>
                        {scoreBadge.emoji}
                      </span>
                    )}
                  </p>
                  <p className="text-xs opacity-80">
                    {isConnecting
                      ? t("Connecting...", "جاري الاتصال...")
                      : agentName
                      ? `${agentName} · ${t("Clinic assistant", "مساعد المركز")}`
                      : t("Customer Service", "خدمة العملاء")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`tel:${CLINIC_PHONE}`}
                  className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                  title={t("Call us", "اتصل بنا")}
                  aria-label="Call clinic"
                >
                  <Phone className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close chat"
                  className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {/* Connecting state */}
              {isConnecting && (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                  <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
                  </div>
                  <p className="text-sm text-gray-600">
                    {t(
                      "Preparing your conversation...",
                      "جاري تجهيز المحادثة..."
                    )}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t("Please wait a moment", "يرجى الانتظار لحظات")}
                  </p>
                </div>
              )}

              {!isConnecting && connectionError && !sessionId && (
                <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p>{t("The chat could not be saved. Reconnect before sending.", "تعذّر حفظ المحادثة. أعد الاتصال قبل الإرسال.")}</p>
                  <button type="button" onClick={() => startSession()} className="mt-3 rounded-lg bg-[#8B6914] px-4 py-2 font-medium text-white">
                    {t("Reconnect", "إعادة الاتصال")}
                  </button>
                </div>
              )}

              {/* Messages */}
              {messages.map((msg, i) => (
                <div key={i}>
                  <div
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-amber-600 text-white rounded-br-md"
                          : "bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100"
                      }`}
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {msg.role === "assistant" ? doctorNameFreeText(msg.content, lang) : msg.content}
                    </div>
                  </div>
                  {/* Lead collected confirmation badge */}
                  {msg.leadCollected && (
                    <div className="flex justify-start mt-1.5">
                      <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
                        <UserCheck className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-xs text-green-700 font-medium">
                          {t(
                            "Your details have been saved — we'll contact you soon",
                            "تم تسجيل بياناتك — سنتواصل معك قريباً"
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Inline contact form */}
              {showContactForm && !leadSaved && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2"
                >
                  <p className="text-xs text-amber-800 font-medium">
                    {t("📋 Leave your details and we'll contact you", "📋 اترك بياناتك وسنتواصل معك")}
                  </p>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder={t("Name", "الاسم")}
                    aria-label="Your name"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-amber-200 bg-white outline-none focus:ring-1 focus:ring-amber-300"
                    dir="auto"
                  />
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder={t("Phone number *", "رقم الهاتف *")}
                    aria-label="Phone number"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-amber-200 bg-white outline-none focus:ring-1 focus:ring-amber-300"
                    dir="ltr"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleContactSubmit}
                      disabled={!contactPhone.trim() || saveLeadMutation.isPending}
                      className="flex-1 py-2 text-xs font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
                      style={{ backgroundColor: BRAND_GOLD }}
                    >
                      {saveLeadMutation.isPending
                        ? t("Sending...", "جاري الإرسال...")
                        : t("Send", "إرسال")}
                    </button>
                    <button
                      onClick={() => setShowContactForm(false)}
                      className="px-3 py-2 text-xs text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t("Later", "لاحقاً")}
                    </button>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-gray-100 p-3 bg-white">
              {sendError && <p role="alert" className="mb-2 text-xs text-red-700">{t("Press Send to safely retry this message.", "اضغط إرسال لإعادة محاولة هذه الرسالة بأمان.")}</p>}
              {/* Offer contact entry immediately when details are requested; keep the existing later shortcut. */}
              {(messages.some(m => m.role === "assistant" && /your name|phone number|اسمك|رقم جوالك/i.test(m.content)) || messages.filter((m) => m.role === "user").length >= 3) && !leadSaved && !showContactForm && (
                <button
                  onClick={() => setShowContactForm(true)}
                  className="w-full mb-2 py-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  {t("📞 Leave your number for our team to contact you", "📞 اترك رقمك ليتواصل معك فريقنا")}
                </button>
              )}
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  maxLength={4000}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isConnecting
                      ? t("Please wait...", "يرجى الانتظار...")
                      : t("Type your message here...", "اكتب رسالتك هنا...")
                  }
                  disabled={isConnecting || !sessionId || !visitorToken}
                  aria-label="Chat message"
                  className="flex-1 px-4 py-2.5 rounded-full bg-gray-100 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  dir="auto"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping || isConnecting || !sessionId || !visitorToken}
                  aria-label="Send message"
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-40 transition-all hover:scale-105 disabled:hover:scale-100"
                  style={{ backgroundColor: BRAND_GOLD }}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
